import type {
  TypedPropertyFilterQuery,
  TypedPropertyFilterToken,
} from '@risksmart-app/components/src/table/tableUtils';
import type {
  DataSourceField,
  GroupByDatePrecision,
} from '@risksmart-app/shared/reporting/api/schema';
import type { DataType } from '@risksmart-app/shared/reporting/datasets/types';
import dayjs from 'dayjs';
import { getFieldUniqueId } from 'src/pages/custom-datasources/update/fieldValue';

import { tablePropertyFilterDateFormat } from '@/utils/dateUtils';

/**
 * Creates a filter property query based on a category value and date precision
 * @param param0
 * @returns
 */
export const getFilterPropertyForCategory = ({
  value,
  x1FieldDatePrecision,
  x1Field,
  x1FieldType,
}: {
  value: unknown;
  x1FieldDatePrecision?: GroupByDatePrecision | null;
  x1Field: DataSourceField;
  x1FieldType: DataType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): TypedPropertyFilterQuery<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tokenGroups: TypedPropertyFilterToken<any>[] = [];

  if (x1FieldType === 'date') {
    if (value === null) {
      tokenGroups.push({
        propertyKey: getFieldUniqueId(x1Field),
        operator: '=',
        value: value,
      });
    } else {
      const precision = x1FieldDatePrecision ?? 'day';
      const start = dayjs(value as string).startOf(precision);
      const end = start.add(1, precision);

      tokenGroups.push({
        propertyKey: getFieldUniqueId(x1Field),
        operator: '>=',
        value: start.format(tablePropertyFilterDateFormat),
      });
      tokenGroups.push({
        propertyKey: getFieldUniqueId(x1Field),
        operator: '<',
        value: end.format(tablePropertyFilterDateFormat),
      });
    }
  } else {
    tokenGroups.push({
      propertyKey: getFieldUniqueId(x1Field),
      operator: '=',
      value,
    });
  }

  return {
    operation: 'and',
    tokenGroups,
    tokens: [],
  };
};

/**
 * Appends the dashboard tag filter (resolved to tag names) as an OR group onto a
 * drill-through filter query, so the CDS detail page's list reflects the same
 * tag-filtered set as the bar the user clicked. The detail page's property
 * filter matches tags by name, and ORing mirrors the bar's any-of semantics.
 * Returns the query unchanged when no tags are selected.
 */
export const withDashboardTagTokens = (
  query: TypedPropertyFilterQuery<Record<string, unknown>>,
  tagNames: string[]
): TypedPropertyFilterQuery<Record<string, unknown>> => {
  if (tagNames.length === 0) {
    return query;
  }

  return {
    ...query,
    tokenGroups: [
      ...(query.tokenGroups ?? []),
      {
        operation: 'or',
        tokens: tagNames.map((name) => ({
          propertyKey: getFieldUniqueId({
            dataSourceIndex: 0,
            fieldId: 'tags',
          }),
          operator: ':',
          value: name,
        })),
      },
    ],
  };
};
